import React, { useState, useEffect, useCallback } from "react";
import {
  AppBar,
  Typography,
  Button,
  TextField,
  Box,
  Breadcrumbs,
  Autocomplete,
  InputAdornment,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useStyles } from "../../assets/styles/Theme.tsx";
import { externalgetApi, externalpostApi } from "../../services/externalApis.tsx";
import {
  SETTING_API_21,
  ITEM_META_API,
  ITEM_API_01,
} from "../../api/ApiModel.tsx";
import ViewDataGrid from "../../components/grid/ViewDataGrid.tsx";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import { usePageData } from "../../utils/common/PageDataContext.tsx";
import { CON_STATUS_ACTIVE, CON_STATUS_DELETED, CON_VALUE_ZERO } from "../../utils/sys/Constants.tsx";
import { useSelector } from "react-redux";
import SystemAlert from "../../components/common/SystemAlert.tsx";
// Import messages
import {
  Messages,
  MessageCategory,
  UITextMessages,
} from "../../utils/sys/Messages.tsx";

// Types and Interfaces
interface MainCategory {
  maincatcode: number;
  maincatname: string;
  descrip?: string;
  sts?: string;
  enuser?: string;
  endate?: string | null;
}

interface Category {
  catcode: number;
  catname: string;
  maincatcode: number;
  descrip?: string;
  sts?: string;
  enuser?: string;
  endate?: string | null;
}

interface SubCategory {
  subcatcode: number;
  subcatname: string;
  catcode: number;
  maincatcode: number;
  descrip?: string;
  sts?: string;
  enuser?: string;
  endate?: string | null;
}

interface GenericName {
  genericcode: number;
  genericname: string;
  subcatcode: number;
  descrip?: string;
  compcode?: string;
  loccode?: string;
  sts?: string;
  enuser?: string;
  endate?: string;
}

interface EnhancedGenericName extends GenericName {
  subcatname: string;
  catcode: number;
  maincatcode: number;
  catname: string;
  maincatname: string;
}

interface Item {
  itemcode: number;
  itemname: string;
  genericcode: number;
  genericname: string;
  [key: string]: any;
}

interface ItemMetaResponse {
  brands?: any[];
  maincategories?: MainCategory[];
  categories?: Category[];
  subcategories?: SubCategory[];
  genericnames?: GenericName[];
  packages?: any[];
  measuringunits?: any[];
  itemvariantstypes?: any[];
}

interface UserParams {
  LOCCODE?: string;
  STAFFMAPLOCODE?: string;
  COMPCODE?: string;
  USERNAME?: string;
}

interface UserState {
  USER_PARAM?: UserParams;
}

// Define MenuItemType to match what ViewDataGrid expects
interface MenuItemType {
  text: string;
  icon: React.ReactElement;
  onClick: (id: string | number) => void;  // This accepts string | number
}

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
    status?: number;
  };
  message?: string;
}

interface DeletePayload {
  COMPCODE?: string;
  LOCCODE?: string;
  genericcode: number;
  subcatcode: number;
  genericname: string;
  descrip?: string;
  sts: string;
  enuser: string;
  MODE: string;
}

interface PageData {
  title: string;
}

interface Column {
  field: string;
  headerName: string;
  flex?: number;
  width?: number;
}

interface RowWithId extends EnhancedGenericName {
  id: number;
}

interface AutocompleteOption {
  label: string;
  value: number;
}

function ViewGenaricName(): React.ReactElement {
  const theme = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();
  const { setPageData } = usePageData();

  const SYS_USER = useSelector((state: { user: UserState }) => state.user) || {};
  const { STAFFMAPLOCODE, COMPCODE, USERNAME } = SYS_USER.USER_PARAM || {};

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCheckingUsage, setIsCheckingUsage] = useState<boolean>(false);
  const [mainCategories, setMainCategories] = useState<MainCategory[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [allSubCategories, setAllSubCategories] = useState<SubCategory[]>([]);

  const [selectedMainCategory, setSelectedMainCategory] = useState<number>(0);
  const [selectedCategory, setSelectedCategory] = useState<number>(0);
  const [selectedSubCategory, setSelectedSubCategory] = useState<number>(0);

  const [data, setData] = useState<EnhancedGenericName[]>([]);
  const [filteredRows, setFilteredRows] = useState<EnhancedGenericName[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<MessageCategory.SUCCESS | MessageCategory.ERROR | MessageCategory.WARNING>(MessageCategory.SUCCESS);
  const [alertMessage, setAlertMessage] = useState<string>("");

  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedItem, setSelectedItem] = useState<EnhancedGenericName | null>(null);

  const showAlert = useCallback((msg: string, type: MessageCategory.SUCCESS | MessageCategory.ERROR | MessageCategory.WARNING): void => {
    setAlertMessage(msg);
    setAlertType(type);
    setAlertOpen(true);
  }, []);

  useEffect(() => {
    setPageData({ title: Messages.genericName.pageTitle.view });
  }, [setPageData]);

  // Function to check if generic name is used in any items
  const checkGenericUsage = async (genericcode: number): Promise<boolean> => {
    try {
      setIsCheckingUsage(true);
      
      // Fetch items with the same genericcode
      const items = await externalgetApi(ITEM_API_01, "get", {
        COMPCODE,
        LOCCODE: STAFFMAPLOCODE,
        STS: CON_STATUS_ACTIVE,
      });

      // Check if any items use this generic name
      const isUsed = items?.some((item: Item) => 
        Number(item.genericcode) === Number(genericcode)
      ) || false;

      return isUsed;
    } catch (error) {
      console.error("Error checking generic name usage:", error);
      return false;
    } finally {
      setIsCheckingUsage(false);
    }
  };

  // ================= INITIAL DATA LOAD USING ITEM_META_API =================
  useEffect(() => {
    const fetchAllData = async (): Promise<void> => {
      if (!COMPCODE || !STAFFMAPLOCODE) return;

      try {
        setIsLoading(true);

        // Fetch all metadata in a single API call
        const response = await externalgetApi(ITEM_META_API, "get", {
          COMPCODE,
          LOCCODE: STAFFMAPLOCODE,
          STS: CON_STATUS_ACTIVE,
        }) as ItemMetaResponse[];

        console.log("API Response:", response);

        // The API returns an array with a single object
        const data = response && response.length > 0 ? response[0] : null;

        if (!data) {
          throw new Error("No data received from API");
        }

        // Extract data from response
        const mains = data.maincategories || [];
        const cats = data.categories || [];
        const subs = data.subcategories || [];
        const generics = data.genericnames || [];

        console.log("Extracted - Mains:", mains.length, "Cats:", cats.length, "Subs:", subs.length, "Generics:", generics.length);

        // Set main categories
        setMainCategories(mains);
        
        // Set categories
        setCategories(cats);
        
        // Set all subcategories
        setAllSubCategories(subs);

        // Set default selections - start with first main category if available
        if (mains?.length) {
          const firstMainCatCode = mains[0].maincatcode;
          setSelectedMainCategory(firstMainCatCode);
          
          // Find categories that belong to the first main category
          const categoriesForFirstMain = cats.filter(
            (cat) => cat.maincatcode === firstMainCatCode
          );
          
          // Set first category for this main category if available
          if (categoriesForFirstMain.length) {
            const firstCatCode = categoriesForFirstMain[0].catcode;
            setSelectedCategory(firstCatCode);
            
            // Find subcategories that belong to this category
            const subcatsForFirstCategory = subs.filter(
              (sub) => sub.catcode === firstCatCode && sub.maincatcode === firstMainCatCode
            );
            
            // Set first subcategory if available
            if (subcatsForFirstCategory.length) {
              setSelectedSubCategory(subcatsForFirstCategory[0].subcatcode);
            } else {
              setSelectedSubCategory(0); // No subcategories for this category
            }
          } else {
            setSelectedCategory(0);
            setSelectedSubCategory(0);
          }
        } else {
          setSelectedMainCategory(0);
          setSelectedCategory(0);
          setSelectedSubCategory(0);
        }

        // Create a map for categories to easily get catname by catcode
        const categoryMap: Record<number, string> = {};
        cats.forEach((cat: Category) => {
          if (cat.catcode) {
            categoryMap[cat.catcode] = cat.catname;
          }
        });

        
        // Create a map for main categories
        const mainCategoryMap: Record<number, string> = {};
        mains.forEach((mainCat: MainCategory) => {
          if (mainCat.maincatcode) {
            mainCategoryMap[mainCat.maincatcode] = mainCat.maincatname;
          }
        });

        // Create a map for subcategory names
        const subcatInfoMap: Record<number, {
          subcatname: string;
          catcode: number;
          maincatcode: number;
        }> = {};
        
        subs.forEach((sub: SubCategory) => {
          if (sub.subcatcode) {
            subcatInfoMap[sub.subcatcode] = {
              subcatname: sub.subcatname || '',
              catcode: sub.catcode,
              maincatcode: sub.maincatcode,
            };
          }
        });

        // Enhance generic names with subcategory, category, and main category info
        const enhancedData: EnhancedGenericName[] = generics.map((item: GenericName) => {
          const subcatInfo = item.subcatcode ? subcatInfoMap[item.subcatcode] : null;
          const catcode = subcatInfo?.catcode || 0;
          const maincatcode = subcatInfo?.maincatcode || 0;

          return {
            ...item,
            subcatname: subcatInfo?.subcatname || `Subcat ${item.subcatcode}`,
            catcode: catcode,
            maincatcode: maincatcode,
            catname: categoryMap[catcode] || '',
            maincatname: mainCategoryMap[maincatcode] || '',
          };
        });

        console.log("Enhanced Data:", enhancedData.length);
        setData(enhancedData);
        setFilteredRows(enhancedData);
      } catch (error) {
        console.error("Error loading initial data:", error);
        showAlert(Messages.genericName.error.fetchInitialFailed, MessageCategory.ERROR);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllData();
  }, [COMPCODE, STAFFMAPLOCODE, showAlert]);

  // ================= UPDATE CATEGORY AND SUBCATEGORY WHEN MAIN CATEGORY CHANGES =================
  useEffect(() => {
    if (selectedMainCategory && selectedMainCategory !== 0) {
      // Find categories for the selected main category
      const categoriesForMain = categories.filter(
        (cat) => cat.maincatcode === selectedMainCategory
      );
      
      // Check if current selected category is valid for this main category
      const isCurrentCategoryValid = categoriesForMain.some(
        (cat) => cat.catcode === selectedCategory
      );
      
      // If current category is not valid and there are categories available, set to first
      if (!isCurrentCategoryValid && categoriesForMain.length > 0) {
        const firstCatCode = categoriesForMain[0].catcode;
        setSelectedCategory(firstCatCode);
        
        // Also update subcategory for this new category
        const subcatsForCategory = allSubCategories.filter(
          (sub) => sub.catcode === firstCatCode && sub.maincatcode === selectedMainCategory
        );
        
        if (subcatsForCategory.length > 0) {
          setSelectedSubCategory(subcatsForCategory[0].subcatcode);
        } else {
          setSelectedSubCategory(0);
        }
      } else if (categoriesForMain.length === 0) {
        // No categories for this main category
        setSelectedCategory(0);
        setSelectedSubCategory(0);
      }
    }
  }, [selectedMainCategory, categories, allSubCategories]);

  // ================= UPDATE SUBCATEGORY WHEN CATEGORY CHANGES =================
  useEffect(() => {
    if (selectedCategory && selectedCategory !== 0 && selectedMainCategory) {
      // Find subcategories for the selected category
      const subcatsForCategory = allSubCategories.filter(
        (sub) => sub.catcode === selectedCategory && sub.maincatcode === selectedMainCategory
      );
      
      // Check if current selected subcategory is valid
      const isCurrentSubcatValid = subcatsForCategory.some(
        (sub) => sub.subcatcode === selectedSubCategory
      );
      
      // If current subcategory is not valid and there are subcategories available, set to first
      if (!isCurrentSubcatValid && subcatsForCategory.length > 0) {
        setSelectedSubCategory(subcatsForCategory[0].subcatcode);
      } else if (subcatsForCategory.length === 0) {
        setSelectedSubCategory(0);
      }
    } else {
      setSelectedSubCategory(0);
    }
  }, [selectedCategory, selectedMainCategory, allSubCategories]);

  // ================= FILTER CATEGORIES BY MAIN CATEGORY =================
  const filteredCategories = categories.filter(
    (cat) => cat.maincatcode === selectedMainCategory
  );

  // ================= FILTER SUBCATEGORIES BY CATEGORY =================
  const filteredSubCategories = allSubCategories.filter(
    (sub) =>
      sub.catcode === selectedCategory && 
      sub.maincatcode === selectedMainCategory
  );

  // ================= APPLY ALL FILTERS =================
  useEffect(() => {
    let filtered = [...data];

    // Filter by subcategory
    if (selectedSubCategory && selectedSubCategory !== 0) {
      filtered = filtered.filter(
        (row) => row.subcatcode === selectedSubCategory
      );
    }
    // If no subcategory selected but category is selected, filter by category
    else if (selectedCategory && selectedCategory !== 0) {
      filtered = filtered.filter(
        (row) => row.catcode === selectedCategory
      );
    }
    // If no category selected but main category is selected, filter by main category
    else if (selectedMainCategory && selectedMainCategory !== 0) {
      filtered = filtered.filter(
        (row) => row.maincatcode === selectedMainCategory
      );
    }

    setFilteredRows(filtered);
  }, [selectedMainCategory, selectedCategory, selectedSubCategory, data]);

  // ================= SEARCH =================
  const handleSearchChange = (event: React.SyntheticEvent, value: string | null): void => {
    const searchValue = value || "";
    setSearchQuery(searchValue);

    if (!searchValue) {
      // Reapply filters without search
      let filtered = [...data];
      
      if (selectedSubCategory && selectedSubCategory !== 0) {
        filtered = filtered.filter(
          (row) => row.subcatcode === selectedSubCategory
        );
      } else if (selectedCategory && selectedCategory !== 0) {
        filtered = filtered.filter(
          (row) => row.catcode === selectedCategory
        );
      } else if (selectedMainCategory && selectedMainCategory !== 0) {
        filtered = filtered.filter(
          (row) => row.maincatcode === selectedMainCategory
        );
      }
      
      setFilteredRows(filtered);
      return;
    }

    const q = searchValue.toLowerCase().trim();
    
    // Start with currently filtered data based on category filters
    let baseFiltered = [...data];
    
    if (selectedSubCategory && selectedSubCategory !== 0) {
      baseFiltered = baseFiltered.filter(
        (row) => row.subcatcode === selectedSubCategory
      );
    } else if (selectedCategory && selectedCategory !== 0) {
      baseFiltered = baseFiltered.filter(
        (row) => row.catcode === selectedCategory
      );
    } else if (selectedMainCategory && selectedMainCategory !== 0) {
      baseFiltered = baseFiltered.filter(
        (row) => row.maincatcode === selectedMainCategory
      );
    }
    
    const filtered = baseFiltered.filter((row) => {
      const genericcodeStr = row.genericcode?.toString() || "";
      
      return (
        genericcodeStr.toLowerCase().includes(q) ||
        row.genericname?.toString().toLowerCase().includes(q)
      );
    });

    setFilteredRows(filtered);
  };

  const addToView = (): void => {
    navigate("/creategenericnames", {
      state: {
        existingGenericNames: data,
      },
    });
  };

  // Updated to accept string | number as required by ViewDataGrid
  const handleItemClick = async (id: string | number, action: string): Promise<void> => {
    // Convert id to number for comparison
    const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
    
    const item = data.find((row) => row.genericcode === numericId);
    if (!item) return;

    if (action === Messages.genericName.action.edit) {
      console.log("Editing item:", item);

      navigate("/creategenericnames", {
        state: {
          selectedItem: item,
          actionText: Messages.genericName.action.edit,
          selectedMainCategory: item.maincatcode || 0,
          selectedCategory: item.catcode || 0,
          selectedSubCategory: item.subcatcode || 0,
          existingGenericNames: data,
        },
      });
    }

    if (action === Messages.genericName.action.delete) {
      setSelectedId(numericId);
      setSelectedItem(item);
      setOpenDeleteDialog(true); // Open confirmation dialog immediately
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (!selectedItem) return;

    try {
      setIsLoading(true);
      
      // Check if generic name is used in any items
      const isUsed = await checkGenericUsage(selectedItem.genericcode);
      
      // If generic name is in use, show error message and don't delete
      if (isUsed) {
        showAlert(Messages.genericName.error.deleteInUse, MessageCategory.ERROR);
        setOpenDeleteDialog(false);
        setSelectedId(null);
        setSelectedItem(null);
        return;
      }

      // Validate required parameters
      if (!COMPCODE) {
        throw new Error(Messages.genericName.error.compCodeMissing);
      }
      if (!STAFFMAPLOCODE) {
        throw new Error(Messages.genericName.error.locCodeMissing);
      }
      if (!USERNAME) {
        throw new Error(Messages.genericName.error.usernameMissing);
      }

      const payload: DeletePayload = {
        COMPCODE,
        LOCCODE: STAFFMAPLOCODE,
        genericcode: selectedItem.genericcode,
        subcatcode: selectedItem.subcatcode,
        genericname: selectedItem.genericname,
        descrip: selectedItem.descrip,
        sts: CON_STATUS_DELETED || "D",
        enuser: USERNAME || "Admin",
        MODE: "DELETE",
      };

      console.log("DELETE payload:", payload);
      console.log("DELETE URL:", SETTING_API_21);

      const response = await externalpostApi(SETTING_API_21, payload);

      console.log("DELETE response:", response);

      // Remove from local state
      setData((prev) =>
        prev.filter((r) => r.genericcode !== selectedItem.genericcode)
      );
      setFilteredRows((prev) =>
        prev.filter((r) => r.genericcode !== selectedItem.genericcode)
      );

      showAlert(Messages.genericName.success.delete, MessageCategory.SUCCESS);
      setOpenDeleteDialog(false);
      setSelectedId(null);
      setSelectedItem(null);
    } catch (error) {
      const err = error as ApiError;
      console.error("DELETE API error:", err);

      // Error handling
      let errorMessage = Messages.genericName.error.deleteFailed;

      if (err.response) {
        console.error("Response Status:", err.response.status);
        console.error("Response Data:", err.response.data);

        if (err.response.data) {
          if (typeof err.response.data === "string") {
            if (err.response.data.includes("Object reference not set to an instance of an object")) {
              errorMessage = "Null reference error occurred";
            } else if (err.response.data.trim() !== "") {
              errorMessage = err.response.data;
            }
          } else if ((err.response.data as any).message) {
            errorMessage = (err.response.data as any).message;
          } else if ((err.response.data as any).error) {
            errorMessage = (err.response.data as any).error;
          }
        }

        // Handle specific HTTP status codes
        switch (err.response.status) {
          case 400:
            errorMessage = errorMessage || "Bad request";
            break;
          case 404:
            errorMessage = "Resource not found";
            break;
          case 405:
            errorMessage = "Method not allowed";
            break;
          case 500:
            errorMessage = errorMessage || "Server error";
            break;
          default:
            errorMessage = `${errorMessage} (Status: ${err.response.status})`;
        }
      } else if (err.request) {
        errorMessage = "No response from server";
      } else {
        errorMessage = err.message || errorMessage;
      }

      showAlert(errorMessage, MessageCategory.ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  const cancelDelete = (): void => {
    setOpenDeleteDialog(false);
    setSelectedId(null);
    setSelectedItem(null);
  };

  // Helper functions for Autocomplete
  const getMainCategoryOptions = (): AutocompleteOption[] => {
    return mainCategories.map((mc) => ({
      label: mc.maincatname,
      value: mc.maincatcode,
    }));
  };

  const getCategoryOptions = (): AutocompleteOption[] => {
    return filteredCategories.map((cat) => ({
      label: cat.catname,
      value: cat.catcode,
    }));
  };

  const getSubCategoryOptions = (): AutocompleteOption[] => {
    return filteredSubCategories.map((sub) => ({
      label: sub.subcatname,
      value: sub.subcatcode,
    }));
  };

  const findSelectedMainCategory = (): AutocompleteOption | undefined => {
    const options = getMainCategoryOptions();
    return options.find((opt) => opt.value === selectedMainCategory);
  };

  const findSelectedCategory = (): AutocompleteOption | undefined => {
    const options = getCategoryOptions();
    return options.find((opt) => opt.value === selectedCategory);
  };

  const findSelectedSubCategory = (): AutocompleteOption | undefined => {
    const options = getSubCategoryOptions();
    return options.find((opt) => opt.value === selectedSubCategory);
  };

  // Create menu items with onClick that accepts string | number
  const menuItems: MenuItemType[] = [
    {
      text: Messages.genericName.action.edit,
      icon: <BorderColorOutlinedIcon />,
      onClick: (id: string | number) => handleItemClick(id, Messages.genericName.action.edit),
    },
    {
      text: Messages.genericName.action.delete,
      icon: <DeleteIcon />,
      onClick: (id: string | number) => handleItemClick(id, Messages.genericName.action.delete),
    },
  ];

  const columns: Column[] = [
    { field: "genericcode", headerName: Messages.genericName.column.id, flex: 2 },
    { field: "genericname", headerName: Messages.genericName.column.name, flex: 2 },
    { field: "descrip", headerName: Messages.genericName.column.description, flex: 2 },
  ];

  const rowsWithId: RowWithId[] = filteredRows.map((row) => ({
    ...row,
    id: row.genericcode,
  }));

  const searchOptions: string[] = data?.map((row) => row.genericname?.toString()).filter(Boolean) || [];

  // Process row update function (required by ViewDataGrid)
  const processRowUpdate = async (newRow: any): Promise<any> => {
    console.log("Row update attempted:", newRow);
    return newRow;
  };

  return (
    <>
      <Box sx={styles.box_page_container}>
        <Box sx={styles.box_breadcrumb_view}>
          <Breadcrumbs
            separator="›"
            aria-label="breadcrumb"
            sx={styles.breadcrumb}
          >
            <Link underline="hover" sx={styles.breadcrumb_link}>
              {UITextMessages.breadcrumb.home}
            </Link>
            <Link underline="hover" sx={styles.breadcrumb_link}>
              {UITextMessages.breadcrumb.settings}
            </Link>
            <Link
              underline="hover"
              sx={styles.breadcrumb_link_page}
              aria-current="page"
            >
              {Messages.genericName.breadcrumb.view}
            </Link>
          </Breadcrumbs>
        </Box>

        <AppBar sx={styles.appbar}>
          <Grid
            container
            spacing={2}
            sx={{
              ...styles.appbar_grid_container,
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {/* Left: Title */}
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 4, xl: 4 }}>
              <Typography sx={styles.appbar_title}>
                {Messages.genericName.tableTitle}
              </Typography>
            </Grid>

            {/* Right: Filters + Search + Add */}
            <Grid size={{ xs: 12, sm: 6, md: 8, lg: 8, xl: 8 }} sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
              {/* Main Category Filter */}
              <Autocomplete
                id="act_maincategory"
                options={getMainCategoryOptions()}
                getOptionLabel={(option: AutocompleteOption) => option.label || ""}
                disableClearable={true}
                value={findSelectedMainCategory() || null}
                onChange={(_, newValue: AutocompleteOption | null) => {
                  setSelectedMainCategory(newValue ? newValue.value : 0);
                  setSelectedCategory(0);
                  setSelectedSubCategory(0);
                }}
                sx={{ minWidth: 180 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Main Category"
                    size="small"
                    fullWidth
                  />
                )}
              />

              {/* Category Filter */}
              <Autocomplete
                id="act_category"
                options={getCategoryOptions()}
                getOptionLabel={(option: AutocompleteOption) => option.label || ""}
                disableClearable={true}
                value={findSelectedCategory() || null}
                onChange={(_, newValue: AutocompleteOption | null) => {
                  setSelectedCategory(newValue ? newValue.value : 0);
                  setSelectedSubCategory(0);
                }}
                sx={{ minWidth: 180 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Category"
                    size="small"
                    fullWidth
                  />
                )}
              />

              {/* SubCategory Filter */}
              <Autocomplete
                id="act_subcategory"
                options={getSubCategoryOptions()}
                getOptionLabel={(option: AutocompleteOption) => option.label || ""}
                disableClearable={true}
                value={findSelectedSubCategory() || null}
                onChange={(_, newValue: AutocompleteOption | null) => {
                  setSelectedSubCategory(newValue ? newValue.value : 0);
                }}
                sx={{ minWidth: 180 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Sub Category"
                    size="small"
                    fullWidth
                  />
                )}
              />

              {/* Search Autocomplete */}
              <Autocomplete
                freeSolo
                id="act_appbarsearch"
                fullWidth
                disableClearable
                value={searchQuery}
                onInputChange={handleSearchChange}
                sx={{ ...styles.ipt_appbar_search_auto, minWidth: 200 }}
                options={data?.map((row) => row.catname) || []}
                renderInput={(params) => {
                  const { id, ...restParams } = params;
                  return (
                    <TextField
                      id="txt_appbarsearch"
                      {...restParams}
                      placeholder={Messages.mainCategory.placeholder.search}
                      size="small"
                      sx={styles.ipt_appbar_search}
                      InputProps={{
                        ...restParams.InputProps,
                        type: "search",
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              id="btn_appbarsearch"
                              sx={styles.btn_appbar_search_icon}
                            >
                              <SearchIcon id="ico_search" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  );
                }}
              />

              {/* Add Button */}
              <Button
  id="btn_add"
  variant="contained"
  startIcon={<AddCircleOutlineIcon />}
  size="small"
  sx={{ 
    height: 40,
    textTransform: 'none' // This prevents uppercase transformation
  }}
  onClick={addToView}
  disabled={isLoading}
>
  {UITextMessages.button.add}
</Button>
            </Grid>
          </Grid>
        </AppBar>

        <Box sx={styles.view_page_container}>
          <ViewDataGrid
            columns={columns}
            rows={rowsWithId}
            menuItems={menuItems}
            tabletitle={Messages.genericName.tableTitle}
            gridid="genericcode"
            processRowUpdate={processRowUpdate}
          />
        </Box>
      </Box>

      <SystemAlert
        open={alertOpen}
        type={alertType}
        message={alertMessage}
        onClose={() => setAlertOpen(false)}
      />

      <Dialog
        id="dlg_delete"
        open={openDeleteDialog}
        onClose={cancelDelete}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">{Messages.genericName.dialog.deleteTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedItem ? (
              `Are you sure you want to delete the generic name "${selectedItem.genericname}"?`
            ) : (
              Messages.genericName.dialog.deleteConfirmation
            )}
          </DialogContentText>
          {/* {selectedItem?.descrip && (
            <DialogContentText sx={{ mt: 2, fontStyle: 'italic' }}>
              Description: {selectedItem.descrip}
            </DialogContentText>
          )} */}
        </DialogContent>
        <DialogActions>
          <Button
            id="btn_cancel"
            onClick={cancelDelete}
            variant="outlined"
            sx={{ ...styles.btn_secondary, ...styles.btn_add_cancel }}
            disabled={isLoading || isCheckingUsage}
          >
            {UITextMessages.button.cancel}
          </Button>
          <Button
            id="btn_confirmdelete"
            onClick={confirmDelete}
            color="error"
            sx={{ ...styles.btn_primary, ...styles.btn_add_confirm }}
            variant="contained"
            disabled={isLoading || isCheckingUsage}
          >
            {isLoading ? "Deleting..." : UITextMessages.button.delete}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ViewGenaricName;